import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import {
  AppointmentForUserCalendarResponse,
  AppointmentsClient,
  WorkingTimeClient,
} from '../../model/fss.api';
import { Arrow } from '../../icons/arrow/arrow';
import { Clock } from '../../icons/clock/clock';
import { ClientAppointmentDetailsDrawer } from '../client-appointment-details-drawer/client-appointment-details-drawer';
import { PageComponent } from '../page/page';
import {
  eventRanges,
  fitSlotTimes,
  MinuteRange,
  slotTimes,
  wholeDays,
  workingHoursRanges,
} from '../../services/calendar-hours';
import { getDateFormatedSr, getDayMonthSr, getMonthShortSr, getMonthYearSr } from '../../services/date.service';

@Component({
  standalone: true,
  selector: 'app-client-appointments-calendar',
  imports: [
    FullCalendarModule,
    PageComponent,
    Arrow,
    Clock,
    ClientAppointmentDetailsDrawer,
  ],
  templateUrl: './client-appointments-calendar.html',
  styleUrl: './client-appointments-calendar.scss',
})
export class ClientAppointmentsCalendar {
  private static readonly MOBILE_BREAKPOINT = 768;

  /** Hours shown until the configured working time has loaded. */
  private static readonly DEFAULT_HOURS: MinuteRange = { from: 8 * 60, to: 20 * 60 };

  /** A 1h block is a single 3rem slot: too short for the two-line card's regular padding. */
  private static readonly SHORT_EVENT_MINUTES = 60;

  private _client = inject(AppointmentsClient);
  private _workingTime = inject(WorkingTimeClient);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);

  fc = viewChild<FullCalendarComponent>('fc');

  showCalendar = signal(true);
  currentStart = signal<Date>(new Date());
  currentEnd = signal<Date>(new Date());

  showDrawer = signal(false);
  drawerAppointmentId = signal<string | undefined>(undefined);

  private _slotWindow: MinuteRange = ClientAppointmentsCalendar.DEFAULT_HOURS;
  private _workingHours: MinuteRange[] = [ClientAppointmentsCalendar.DEFAULT_HOURS];
  private _eventHours: MinuteRange[] = [];
  /** ISO weekdays (1 = Mon … 7 = Sun) from Podešavanja; undefined until loaded. */
  private _workingDays: Set<number> | undefined;
  /** Dates closed for the whole day by an admin date override. */
  private _closedDates = new Set<string>();

  constructor() {
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this.showCalendar.set(false));
    this._loadWorkingDays();
  }

  calendarOptions: CalendarOptions = {
    plugins: [timeGridPlugin, interactionPlugin],
    initialView: this._isNarrowScreen() ? 'timeGridThreeDay' : 'timeGridWeek',
    views: {
      timeGridThreeDay: {
        type: 'timeGrid',
        duration: { days: 3 },
      },
    },
    headerToolbar: false,
    // Natural height: the page scrolls, not the grid (avoids scroll-in-scroll on mobile).
    height: 'auto',
    nowIndicator: true,
    allDaySlot: false,
    ...slotTimes(ClientAppointmentsCalendar.DEFAULT_HOURS),
    slotDuration: '01:00:00',
    slotLabelInterval: '01:00:00',
    slotLabelFormat: { hour: 'numeric', meridiem: 'short' },
    dayHeaderContent: (arg) => this._renderDayHeader(arg.date),
    firstDay: 1,
    locale: 'sr-Latn',
    eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    eventDisplay: 'block',
    eventClassNames: (arg) => (this._isShortEvent(arg.event.start, arg.event.end) ? ['fc-event-short'] : []),
    dayCellClassNames: (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []),
    dayHeaderClassNames: (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []),
    windowResize: () => this._handleWindowResize(),
    datesSet: (arg) => this._onDatesSet(arg),
    eventClick: (arg) => this._onEventClick(arg),
    events: (info, success, failure) => {
      // Whole days, so appointments outside the hours currently shown are fetched too.
      const range = wholeDays(info.start, info.end);
      this._client.getAppointmentsForUserCalendar(range.start, range.end).subscribe({
        next: (res) => {
          const events = (res.result ?? []).map((a) => this._toEvent(a));
          success(events);
          this._eventHours = eventRanges(events);
          this._fitHours();
        },
        error: (err) => failure(err),
      });
    },
  };

  titleLabel = computed(() => getMonthYearSr(this.currentStart()));

  rangeLabel = computed(() => {
    const start = this.currentStart();
    const end = new Date(this.currentEnd().getTime() - 1);
    // Same month: '15. – 17. okt 2026' (short enough for the mobile toolbar).
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.getDate()}. – ${getDateFormatedSr(end)}`;
    }
    // Same year: '30. sep – 2. okt 2026'.
    if (start.getFullYear() === end.getFullYear()) {
      return `${getDayMonthSr(start)} – ${getDateFormatedSr(end)}`;
    }
    return `${getDateFormatedSr(start)} – ${getDateFormatedSr(end)}`;
  });

  badgeMonth = computed(() => getMonthShortSr(this.currentStart()));

  badgeDay = computed(() => String(this.currentStart().getDate()).padStart(2, '0'));

  prev() {
    this.fc()?.getApi().prev();
  }

  next() {
    this.fc()?.getApi().next();
  }

  today() {
    this.fc()?.getApi().today();
  }

  private _renderDayHeader(date: Date): { domNodes: Node[] } {
    const parts = new Intl.DateTimeFormat('sr-Latn', { weekday: 'short', day: 'numeric' }).formatToParts(date);
    const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const dayNumber = parts
      .filter((p) => p.type === 'day' || p.type === 'literal')
      .map((p) => p.value)
      .join('')
      .trim();

    const weekdaySpan = document.createElement('span');
    weekdaySpan.className = 'fc-day-header-weekday';
    weekdaySpan.textContent = weekday;

    const dayNumberSpan = document.createElement('span');
    dayNumberSpan.className = 'fc-day-header-daynum';
    dayNumberSpan.textContent = ` ${dayNumber}`;

    const wrapper = document.createElement('span');
    wrapper.append(weekdaySpan, dayNumberSpan);

    return { domNodes: [wrapper] };
  }

  /** Working days and hours as configured by the admin (Podešavanja → radno vreme, neradni dani). */
  private async _loadWorkingDays(): Promise<void> {
    const [wt, overrides] = await Promise.all([
      firstValueFrom(this._workingTime.getWorkingTime()),
      firstValueFrom(this._workingTime.getDateOverrides()),
    ]);
    // All day state first: fitting the hours below already re-renders the grid.
    this._workingDays = new Set(wt.result?.workingDays ?? []);
    this._closedDates = new Set(
      (overrides.result?.dateOverrides ?? [])
        .filter((o) => o.date && !(o.intervals?.length))
        .map((o) => this._dateKey(o.date!)),
    );
    const hours = workingHoursRanges(wt.result, overrides.result?.dateOverrides);
    if (hours.length > 0) {
      this._workingHours = hours;
      this._fitHours();
    }
    // Class-name callbacks only run on render; FullCalendar ignores re-setting the same function,
    // so pass new ones to force the day cells and headers to re-render with the loaded state.
    const api = this.fc()?.getApi();
    api?.setOption('dayCellClassNames', (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []));
    api?.setOption('dayHeaderClassNames', (arg) => (this._isNonWorkingDay(arg.date) ? ['fc-day-non-working'] : []));
  }

  private _fitHours(): void {
    this._slotWindow = fitSlotTimes(this.fc()?.getApi(), this._slotWindow, [
      ...this._workingHours,
      ...this._eventHours,
    ]);
  }

  private _isNonWorkingDay(date: Date): boolean {
    if (this._closedDates.has(this._dateKey(date))) return true;
    if (!this._workingDays || this._workingDays.size === 0) return false;
    const isoDay = date.getDay() === 0 ? 7 : date.getDay();
    return !this._workingDays.has(isoDay);
  }

  private _dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  private _isShortEvent(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return false;
    return end.getTime() - start.getTime() <= ClientAppointmentsCalendar.SHORT_EVENT_MINUTES * 60_000;
  }

  private _isNarrowScreen(): boolean {
    return typeof window !== 'undefined'
      && window.innerWidth < ClientAppointmentsCalendar.MOBILE_BREAKPOINT;
  }

  private _handleWindowResize() {
    const api = this.fc()?.getApi();
    if (!api) return;
    const targetView = this._isNarrowScreen() ? 'timeGridThreeDay' : 'timeGridWeek';
    if (api.view.type !== targetView) {
      api.changeView(targetView);
    }
  }

  private _onDatesSet(arg: DatesSetArg) {
    this.currentStart.set(arg.start);
    this.currentEnd.set(arg.end);
  }

  private _toEvent(a: AppointmentForUserCalendarResponse): EventInput {
    const isMine = a.isMine === true;
    const palette = isMine
      ? { bg: '#EFF4FF', border: '#B2CCFF', text: '#1646A4' }
      : { bg: '#F0F2F5', border: '#E1E2E5', text: '#414651' };
    return {
      id: a.id,
      title: '',
      start: a.from,
      end: a.to,
      backgroundColor: palette.bg,
      borderColor: palette.border,
      textColor: palette.text,
      extendedProps: {
        isMine,
        from: a.from,
        to: a.to,
      },
    };
  }

  private _onEventClick(arg: EventClickArg) {
    const isMine = arg.event.extendedProps['isMine'] as boolean;
    const id = arg.event.id;
    if (!isMine || !id) return;
    this.drawerAppointmentId.set(id);
    this.showDrawer.set(true);
  }

  onDrawerAction() {
    this.fc()?.getApi().refetchEvents();
  }

  formatEventDate(date: Date | null): string {
    if (!date) return '';
    const weekday = new Intl.DateTimeFormat('sr-Latn', { weekday: 'short' }).format(date);
    return `${weekday}, ${getDateFormatedSr(date)}`;
  }

  formatTime(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

}
